import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Stethoscope, Smartphone, Truck, Check, GraduationCap, Sparkles } from "lucide-react";
import { IMAGES } from "../lib/constants";

const PILLARS = [
  {
    icon: Stethoscope,
    eyebrow: "HCP Connect",
    title: "Doctor & specialist engagement, on the ground.",
    body: "Young pharmacy graduates from Tier 2–3 cities, comprehensively trained, locally fluent. Consolidating brand-doctor relationships one extra-urban market at a time.",
    metric: { label: "HCP Touchpoints", value: "55,000+" },
    bullets: [
      "Direct doctor & specialist engagement",
      "Brand consolidation relationships",
      "Medical training programs & CMEs",
      "Clear career progression for field reps",
    ],
  },
  {
    icon: Smartphone,
    eyebrow: "Digital Connect",
    title: "Remote engagement & scalability — without proportional cost.",
    body: "An in-house digital team covering pan-India, layering always-on touch over field reach. 40–50 digital calls per person per day, at a fraction of the field cost.",
    metric: { label: "Digital Calls / Day", value: "40–50" },
    bullets: [
      "40–50 digital calls per person daily",
      "Virtual CME programs & medical updates",
      "WhatsApp-based engagement & loyalty",
      "Practice management & certification",
    ],
  },
  {
    icon: Truck,
    eyebrow: "Channel Connect",
    title: "Distribution optimized — availability, compliance, zero stockouts.",
    body: "Strategic hub-chemist partnerships ensure product is on the shelf when prescriptions hit. 'Availability is God' — and we treat it that way.",
    metric: { label: "Chemist Network", value: "60,000+" },
    bullets: [
      "End-to-end logistics management",
      "Inventory optimization & monitoring",
      "GST & compliance assistance",
      "Prevention of expiries & stockouts",
    ],
  },
];

const STEPS = [
  { n: "01", title: "Portfolio Assessment", body: "We analyze your mature & tail-end brand portfolio to identify revenue opportunities and gaps in coverage." },
  { n: "02", title: "Delta Force Deployment", body: "Trained young professionals from Tier 2–3 cities are mapped to target markets and brands." },
  { n: "03", title: "3D Phygital Execution", body: "HCP Connect + Digital Connect + Channel Connect deliver complete brand engagement." },
  { n: "04", title: "Performance-Based Results", body: "Revenue-sharing model aligns incentives — you pay for growth, not just effort." },
];

export default function Approach() {
  return (
    <main className="pt-32 md:pt-40 pb-24" data-testid="approach-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        {/* HERO */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-20 md:mb-28">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Our Approach</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.98] text-slate-900">
              The Delta <br />
              <span className="font-editorial italic font-medium text-slate-700">Field Force</span> Model.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              A new generation of pharma field force, engineered for the markets traditional reps
              overlook — and priced for the economics Indian pharma actually needs.
            </p>
          </div>
        </div>

        {/* Delta Field Force Explainer */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24 md:mb-32 items-center" data-testid="approach-delta-section">
          <div className="lg:col-span-6">
            <img
              src={IMAGES.network}
              alt="Network of young pharma professionals across India"
              className="w-full aspect-[4/3] object-cover bg-slate-100"
            />
          </div>
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-[1.08] mb-5">
              Young, local, <br />
              <span className="font-editorial italic font-medium text-slate-700">highly motivated.</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
              Pharmacy graduates from Tier 2–3 cities — trained comprehensively on therapy areas,
              compliance, channel dynamics, and digital tools. They speak the local language. They
              know the local doctor. They build the trust a visiting metro rep never can.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 border border-slate-200">
                <GraduationCap className="h-6 w-6 text-indigo-500 mb-3" strokeWidth={1.5} />
                <div className="font-display font-bold text-slate-900">Tier 2–3 Talent</div>
                <div className="text-sm text-slate-600 mt-1">Pharmacy graduates with local fluency.</div>
              </div>
              <div className="p-5 border border-slate-200">
                <Sparkles className="h-6 w-6 text-teal-600 mb-3" strokeWidth={1.5} />
                <div className="font-display font-bold text-slate-900">Performance Economics</div>
                <div className="text-sm text-slate-600 mt-1">Revenue-share aligned to growth.</div>
              </div>
            </div>
          </div>
        </section>

        {/* 3D Phygital */}
        <section className="mb-24 md:mb-32" data-testid="approach-phygital-section">
          <div className="max-w-3xl mb-14 md:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">
              3D Phygital Engine
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.04]">
              HCP + Digital + Channel <br />
              <span className="font-editorial italic font-medium text-slate-700">= complete revitalization.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.eyebrow}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`approach-pillar-${i}`}
                  className="bg-white p-8 md:p-10"
                >
                  <div className="flex items-center justify-between mb-8">
                    <Icon className="h-7 w-7 text-teal-600" strokeWidth={1.5} />
                    <div className="text-right">
                      <div className="font-display font-extrabold text-2xl tnum text-slate-900">{p.metric.value}</div>
                      <div className="text-[10px] tracking-[0.14em] uppercase text-slate-500 font-semibold">
                        {p.metric.label}
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700 mb-2">
                    {p.eyebrow}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-4 leading-[1.2]">
                    {p.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{p.body}</p>
                  <ul className="space-y-2.5 border-t border-slate-200 pt-5">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" strokeWidth={2} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Process */}
        <section className="mb-24 md:mb-32" data-testid="approach-process-section">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">The Process</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.04]">
              4 steps to revitalize <br />
              <span className="font-editorial italic font-medium text-slate-700">your brands.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`approach-step-${i}`}
                className="bg-white p-8 md:p-10"
              >
                <div className="font-display font-extrabold text-5xl text-slate-200 tnum mb-8">{s.n}</div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-3">{s.title}</h3>
                <p className="text-[15px] text-slate-600 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0B0F19] text-white p-10 md:p-16 text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-300 mb-3">
            Ready when you are
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-extrabold mb-4 max-w-2xl mx-auto">
            See how the Delta Field Force fits <span className="font-editorial italic font-medium text-slate-300">your portfolio.</span>
          </h3>
          <Link to="/#contact" className="inline-flex items-center gap-2 btn-accent px-6 py-3.5 text-sm font-semibold mt-4">
            Request Portfolio Analysis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </main>
  );
}
